import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Notice,
  Badge,
  Select,
  SelectItem,
  Button,
  Icon,
  Breadcrumbs,
  Breadcrumb,
} from "@conuti-das/prince-ui";
import { Flex, princeIcon } from "@/ui-prince";
import ticketRunsData from "@/data/generated/ticket-runs.json";
import type { TicketRun, Ticket, TicketRelevance, ReviewState } from "@/types/tickets";
import { useReviewState } from "@/hooks/useReviewState";
import { TicketReviewDialog } from "@/components/review/TicketReviewDialog";
import { ReviewTable } from "@/components/review/ReviewTable";
import { jiraApi, JiraApiError } from "@/lib/jiraApi";
import { ticketKey as makeTicketKey } from "@/types/tickets";

const RELEVANCES: TicketRelevance[] = ["kritisch", "hoch", "normal", "gering", "unbewertet"];
const STATE_TO_OBJ: Record<ReviewState, "Positive" | "Critical" | "Negative" | "None" | "Information"> = {
  pending: "None",
  approved: "Positive",
  rejected: "Negative",
  "in-jira": "Information",
};

/** Badge tone per review state */
const STATE_TONE: Record<ReviewState, "neutral" | "green" | "red" | "orange" | "blue"> = {
  pending: "neutral",
  approved: "green",
  rejected: "red",
  "in-jira": "blue",
};

export function ReviewQueuePage() {
  const { runId: runIdParam } = useParams<{ runId?: string }>();
  const runs = ticketRunsData as TicketRun[];

  const activeRun = useMemo(() => {
    if (runIdParam) return runs.find((r) => r.run_id === runIdParam);
    return runs[0];
  }, [runs, runIdParam]);

  const [filter, setFilter] = useState<"all" | TicketRelevance>("all");
  const [stateFilter, setStateFilter] = useState<"all" | ReviewState>("all");
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ kind: "success" | "error" | "info"; text: string } | null>(null);

  const review = useReviewState(activeRun?.run_id ?? "");

  if (!activeRun) {
    return (
      <div className="page">
        <Notice tone="info">
          Noch keine Ticket-Runs vorhanden. Erzeuge welche mit dem
          <code> erzeuge-regulatorik-ticket </code> Skill.
        </Notice>
      </div>
    );
  }

  const reviewTickets = activeRun.tickets.filter((t) => t.review_needed);
  const filtered = reviewTickets.filter((t) => {
    if (filter !== "all" && t.business_relevance !== filter) return false;
    if (stateFilter !== "all") {
      const d = review.getDecision(t);
      const state = d?.state ?? "pending";
      if (state !== stateFilter) return false;
    }
    return true;
  });

  const counts = review.counts(reviewTickets);

  // Wieviele Tickets sind approved, aber noch nicht in JIRA? Das ist die
  // Zahl, die auf dem Sync-Button steht und auch der Server tatsächlich
  // anlegen würde (Server skipt schon-vorhandene via jira-result.json).
  const syncableCount = useMemo(
    () => reviewTickets.filter((t) => {
      const d = review.getDecision(t);
      return d?.state === "approved" && !d.jira_key;
    }).length,
    [reviewTickets, review]
  );

  const handleSyncToJira = async () => {
    if (!activeRun || syncing) return;
    setSyncing(true);
    setSyncMessage({ kind: "info", text: "Sync läuft..." });
    try {
      // Wir schicken nur den Subset des State, das den Server interessiert
      // (state + edits), gespart die Webapp-internen Felder.
      const payload: Record<string, { state: string; edits?: any }> = {};
      for (const [key, dec] of Object.entries(review.decisions)) {
        payload[key] = { state: dec.state, edits: dec.edits };
      }
      const result = await jiraApi.syncBatch(activeRun.run_id, payload);
      review.applyJiraResult(result.created);
      const newCount = result.created.length;
      const failCount = result.failed.length;
      setSyncMessage({
        kind: failCount > 0 ? "error" : "success",
        text: `Sync fertig: ${newCount} angelegt, ${failCount} failed. Epic: ${result.epic_key ?? "-"}`,
      });
    } catch (exc) {
      const msg = exc instanceof JiraApiError
        ? `${exc.detail} (HTTP ${exc.status})`
        : String(exc);
      setSyncMessage({ kind: "error", text: `Sync fehlgeschlagen: ${msg}` });
    } finally {
      setSyncing(false);
    }
  };

  // Per-Klick aus dem Detail-Dialog: einzelnes Ticket sofort in JIRA anlegen.
  const handleApproveAndSync = async (ticket: Ticket, edits: { title?: string; description?: string }) => {
    if (!activeRun) return;
    // Zuerst lokal als approved markieren (gleiches Verhalten wie pure Approve)
    review.setDecision(ticket, { state: "approved", edits, reviewed_at: new Date().toISOString() });
    setSelected(null);
    setSyncing(true);
    try {
      const key = makeTicketKey(ticket);
      const result = await jiraApi.syncOne(activeRun.run_id, key, edits);
      review.applyJiraResult(result.created);
      const newEntry = result.created.find((e) => e.ticket_key === key);
      if (newEntry) {
        setSyncMessage({ kind: "success", text: `In JIRA: ${newEntry.jira_key}` });
      } else if (result.failed.length) {
        setSyncMessage({ kind: "error", text: `Fehler: ${result.failed[result.failed.length - 1].error}` });
      } else {
        setSyncMessage({ kind: "info", text: "Ticket war bereits in JIRA -- kein neues angelegt." });
      }
    } catch (exc) {
      const msg = exc instanceof JiraApiError ? `${exc.detail} (HTTP ${exc.status})` : String(exc);
      setSyncMessage({ kind: "error", text: `Sync fehlgeschlagen: ${msg}` });
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = () => {
    const json = review.exportRunState();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `review-state-${activeRun.run_id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <Breadcrumbs>
        <Breadcrumb><span>Review-Queue</span></Breadcrumb>
        <Breadcrumb>
          <span>
            {activeRun.meta_run_id
              ? `${activeRun.meta_run_id} (${activeRun.run_id})`
              : activeRun.run_id}
          </span>
        </Breadcrumb>
      </Breadcrumbs>

      <div className="page-header" style={{ marginTop: "var(--s-3)" }}>
        <h2 className="page-title">Review-Queue</h2>
        <div className="page-subtitle">
          Auto-generierte Ticket-Drafts, die manuell durchgesehen werden müssen.
          Markiere als „In JIRA anlegen" oder „Verwerfen" — danach kann das
          <code> ticket-to-jira-adapter </code> Tool die JIRA-Issues anlegen.
        </div>
      </div>

      {/* Run-Auswahl + Status-KPI */}
      <Card style={{ marginBottom: "var(--s-4)" }}>
        <div style={{ padding: "var(--s-3) var(--s-4)" }}>
          <Flex style={{ gap: "var(--s-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "var(--s-1)" }}>
                Run
              </div>
              <Select
                selectedKey={activeRun.run_id}
                onSelectionChange={(id) => {
                  if (id) window.location.hash = `#/review/${id}`;
                }}
                style={{ width: "100%" }}
              >
                {runs.map((r) => {
                  const reviewCount = r.tickets.filter((t) => t.review_needed).length;
                  // Bevorzugt den Meta-Run-Identifier (run-202610-2026-05-20-003), fällt auf
                  // den dist-Timestamp zurück, wenn kein Meta-Run referenziert ist.
                  const label = r.meta_run_id
                    ? `${r.meta_run_id} (${r.run_id}) · ${reviewCount} review-tickets`
                    : `${r.run_id} · ${reviewCount} review-tickets`;
                  return (
                    <SelectItem key={r.run_id} id={r.run_id}>
                      {label}
                    </SelectItem>
                  );
                })}
              </Select>
            </div>

            <KPIChip label="Pending" count={counts.pending} state="pending" />
            <KPIChip label="Approved" count={counts.approved} state="approved" />
            <KPIChip label="Rejected" count={counts.rejected} state="rejected" />
            <KPIChip label="In JIRA" count={counts["in-jira"]} state="in-jira" />
          </Flex>

          {activeRun.manifest && (
            <Flex style={{ gap: "var(--s-3)", marginTop: "var(--s-3)", flexWrap: "wrap" }}>
              <span className="muted" style={{ fontSize: "0.78rem" }}>
                Formatversion: <strong>{activeRun.manifest.formatversion}</strong>
                {" · "}
                Quelle: {activeRun.manifest.source_changelog?.split(/[/\\]/).pop()}
                {" · "}
                Phase: {activeRun.manifest.phase}
                {" · "}
                Threshold: {activeRun.manifest.threshold}
              </span>
            </Flex>
          )}
        </div>
      </Card>

      {/* Toolbar mit Filtern und Export */}
      <Flex style={{ marginBottom: "var(--s-3)", gap: "var(--s-3)", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontWeight: 600 }}>{filtered.length} von {reviewTickets.length}</span>
        <div style={{ flex: 1 }} />

        <span className="muted" style={{ fontSize: "0.78rem" }}>Relevanz:</span>
        <Select
          selectedKey={filter}
          onSelectionChange={(v) => setFilter((v != null ? String(v) : "all") as any)}
        >
          <SelectItem id="all">Alle</SelectItem>
          {RELEVANCES.map((r) => (
            <SelectItem key={r} id={r}>{r}</SelectItem>
          ))}
        </Select>

        <span className="muted" style={{ fontSize: "0.78rem", marginLeft: "var(--s-3)" }}>Status:</span>
        <Select
          selectedKey={stateFilter}
          onSelectionChange={(v) => setStateFilter((v != null ? String(v) : "all") as any)}
        >
          <SelectItem id="all">Alle</SelectItem>
          <SelectItem id="pending">Pending</SelectItem>
          <SelectItem id="approved">Approved</SelectItem>
          <SelectItem id="rejected">Rejected</SelectItem>
        </Select>

        <div style={{ flex: 1 }} />
        <Button
          variant="filled"
          isDisabled={syncing || syncableCount === 0}
          onPress={() => void handleSyncToJira()}
        >
          <Icon name={princeIcon("connected")} size={16} />
          {syncing ? "Sync läuft..." : `In JIRA anlegen (${syncableCount})`}
        </Button>
        <Button variant="tinted" onPress={handleExport}>
          <Icon name={princeIcon("download")} size={16} />
          review-state.json exportieren
        </Button>
        <Button variant="plain" onPress={review.resetRun}>
          <Icon name={princeIcon("refresh")} size={16} />
          Run zurücksetzen
        </Button>
      </Flex>

      {/* Tabelle */}
      <ReviewTable
        tickets={filtered}
        getDecision={review.getDecision}
        onSelect={setSelected}
        stateToObj={STATE_TO_OBJ}
      />

      {/* Sync-Status-Strip (zeigt Backend-Antwort der letzten Sync-Aktion) */}
      {syncMessage && (
        <div style={{ marginTop: "var(--s-4)" }}>
          <Notice
            tone={syncMessage.kind === "success" ? "positive" : syncMessage.kind === "error" ? "negative" : "info"}
          >
            {syncMessage.text}
            <Button variant="plain" onPress={() => setSyncMessage(null)} style={{ marginLeft: "var(--s-2)" }}>
              ✕
            </Button>
          </Notice>
        </div>
      )}

      {/* Fallback-Hinweis falls die Webapp den Runner-Backend nicht erreicht --
          dann ist Export+CLI der manuelle Workaround. */}
      {(counts.approved + counts["in-jira"] > 0) && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">
            {counts.approved} approved · {counts["in-jira"]} bereits in JIRA · {counts.rejected} rejected.
            Bei Backend-Ausfall: <code>review-state.json exportieren</code> + CLI
            {" "}<code>maco-ticket-to-jira apply --run {activeRun.run_id} --state &lt;datei&gt;</code>.
          </Notice>
        </div>
      )}

      {/* Detail-Dialog */}
      {selected && (
        <TicketReviewDialog
          ticket={selected}
          decision={review.getDecision(selected)}
          onClose={() => setSelected(null)}
          onApprove={(edits) => {
            review.setDecision(selected, { state: "approved", edits, reviewed_at: new Date().toISOString() });
            setSelected(null);
          }}
          onApproveAndSync={(ticket, edits) => handleApproveAndSync(ticket, edits)}
          onReject={(reason) => {
            review.setDecision(selected, {
              state: "rejected",
              reject_reason: reason,
              reviewed_at: new Date().toISOString(),
            });
            setSelected(null);
          }}
          onReset={() => {
            review.clearDecision(selected);
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function KPIChip({ label, count, state }: {
  label: string;
  count: number;
  state: ReviewState;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 90 }}>
      <span className="muted" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <Flex alignItems="center" style={{ gap: 6 }}>
        <Badge tone={STATE_TONE[state]}>
          <span style={{ fontWeight: 700, fontSize: "1rem" }}>{count}</span>
        </Badge>
      </Flex>
    </div>
  );
}
